import * as Yup from 'yup';
import { ERRORS } from '../constants/base';
import { manualAddressFieldTests } from './manualAddressRules';

/**
 * Rules for the "Left town" step's staged `new_address`.
 *
 * Exported (rather than written inline in useVisitReportForm) so the tests exercise the
 * REAL schema instead of a hand-copied replica that can silently drift — the same reason
 * manualAddressFieldTests lives on its own.
 */

/**
 * Did the user actually enter an address?
 *
 * "Left town" reports that a household MOVED. Often the worker knows they moved but not
 * where to — so the address is optional and a blank one must not block the report. This
 * decides blank-vs-entered. Anything typed into the address box counts, so a half-finished
 * entry still has to be completed (picked from Google, saved as text-only, or switched to
 * manual) rather than being saved as unusable free text. Choosing a Home/Work `type` counts
 * too: picking one is a deliberate act that starts the address.
 *
 * `apartment_number` and `info_note` deliberately do NOT count. Neither is an address, and a
 * note is exactly what someone writes when they know a household moved but not where to
 * ("forwarding address unknown") — treating that as a start would re-create the very trap
 * this rule exists to remove.
 *
 * `type` is only safe to count because nothing sets it implicitly on THIS field: the Left
 * Town checkbox stages VISITS_ADDRESS with `type: ''` and only the user's own selection
 * fills it. (The `lockHomeType` effect that forces `type: 'Home'` applies solely to the
 * separate `updated_home_address` correction field, which is always-required anyway.)
 */
export const visitAddressHasContent = (address: any): boolean => {
  if (!address || typeof address !== 'object') return false;
  const filled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';
  return (
    filled(address.fullAddress) ||
    filled(address.formatted_address) ||
    filled(address.place_id) ||
    filled(address.latitude) ||
    filled(address.longitude) ||
    filled(address.type) ||
    !!address.is_text_only
  );
};

/**
 * The strict shape applied once an address HAS been entered. Unchanged from what
 * "Left town" always enforced.
 */
export const enteredNewAddressSchema = () =>
  Yup.object()
    .shape({
      type: Yup.string().required(ERRORS.required),
      // A MANUAL address carries its own coordinates and has no place_id by design; only a
      // geocoded address must be picked from the suggestions.
      place_id: Yup.string().when(['is_manual', 'is_text_only'], {
        is: (m: boolean, t: boolean) => !!m || !!t,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(ERRORS.address_not_selected),
      }),
      // A MANUAL address the user is keeping must carry in-range coordinates and a seeded
      // region (parity with addressSchema via the shared manualAddressFieldTests).
      ...manualAddressFieldTests(),
      // A Work address must be attached to a contact — block the save until one is chosen
      // (the backend rejects a contact-less work address on approval anyway).
      person_id: Yup.mixed().when('type', {
        is: (t: string) => t === 'Work',
        then: (schema) =>
          schema.test(
            'work-contact-required',
            'Please select a contact for the work address',
            (v: any) => v !== null && v !== undefined && v !== '' && Number(v) > 0
          ),
        otherwise: (schema) => schema.nullable(),
      }),
      // The API returns `null` (not undefined) when no draft is staged yet — without
      // `.nullable()`, Yup's object schema rejects a literal null as a type mismatch,
      // blocking a save that never touched this field at all.
    })
    .nullable();

/**
 * The validator actually mounted at `new_address`.
 *
 * Blank + "Left town" checked -> nothing to validate, the report saves recording only that
 * the household moved. Entered -> the full strict shape above, so per-field error paths
 * (`new_address.place_id` etc.) still reach the inline messages in the LeftTown editor.
 *
 * Gated on content ALONE, not on `is_left_town`: a leftover/blank `new_address` from a
 * prior unconfirmed visit must never block an unrelated save either.
 */
export const newAddressValidator = () =>
  Yup.lazy((value: any) =>
    visitAddressHasContent(value) ? enteredNewAddressSchema() : Yup.mixed().notRequired()
  );
